import {
  IconArrowLeft,
  IconAlertTriangle,
  IconRefresh,
} from "@tabler/icons-react";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  useNavigate,
} from "@tanstack/react-router";
import { toast } from "sonner";

import type { ClusterFormValues } from "@/k8s-clusters/schemas";

import { useTRPC } from "@/infrastructure/trpc/react";
import { ClusterForm } from "@/k8s-clusters/components/cluster-form";
import { AppLayout } from "@/shared/layout";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shared/ui/alert";
import { Button } from "@/shared/ui/button";

export const Route = createFileRoute(
  "/app/organization/$id/clusters/$clusterId/edit"
)({
  loader: async ({ context, params }) => {
    await context.queryClient
      .query(
        context.trpc.k8sClusters.getByIdWithCredentials.queryOptions(
          {
            organizationId: params.id,
            clusterId: params.clusterId,
          }
        )
      )
      .catch(() => undefined);
  },
  component: EditClusterPage,
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function EditClusterPage() {
  const { id: organizationId, clusterId } =
    Route.useParams();
  const navigate = useNavigate();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { data: clusterData } = useSuspenseQuery(
    trpc.k8sClusters.getByIdWithCredentials.queryOptions({
      organizationId,
      clusterId,
    })
  );

  const cluster = clusterData?.cluster;

  const updateCluster = useMutation(
    trpc.k8sClusters.update.mutationOptions({
      onSuccess: async () => {
        toast.success("K8s cluster updated successfully");
        await queryClient.invalidateQueries(
          trpc.k8sClusters.list.queryOptions({
            organizationId,
          })
        );
        await queryClient.invalidateQueries(
          trpc.k8sClusters.getById.queryOptions({
            organizationId,
            clusterId,
          })
        );
        void navigate({
          to: "/app/organization/$id/settings/k8s-clusters",
          params: { id: organizationId },
        });
      },
      onError: (error) => {
        toast.error(
          error.message ?? "Failed to update cluster"
        );
      },
    })
  );

  const handleSubmit = async (
    values: ClusterFormValues
  ) => {
    await updateCluster.mutateAsync({
      organizationId,
      clusterId,
      ...values,
    });
  };

  const initialValues: ClusterFormValues = {
    name: cluster?.name ?? "",
    clusterUrl: cluster?.clusterUrl ?? "",
    serviceAccountToken: cluster?.serviceAccountToken ?? "",
    caCertificate: cluster?.caCertificate ?? "",
    tokenIssuerToken: cluster?.tokenIssuerToken ?? "",
  };

  const tokenStatus = cluster?.tokenStatus;
  const hasAutoRefresh = cluster?.hasAutoRefresh;

  return (
    <AppLayout
      title="Edit K8s Cluster"
      headerActions={
        <Link
          to="/app/organization/$id/settings/k8s-clusters"
          params={{ id: organizationId }}
        >
          <Button variant="outline" size="sm">
            <IconArrowLeft className="mr-2 size-4" />
            Back to Settings
          </Button>
        </Link>
      }
    >
      <div className="mx-auto max-w-5xl space-y-6">
        {/* Token Status Alert */}
        {tokenStatus?.status === "expired" && (
          <Alert variant="destructive">
            <IconAlertTriangle className="size-4" />
            <AlertTitle>Token Expired</AlertTitle>
            <AlertDescription>
              The service account token has expired. The
              cluster connection will fail until the token
              is updated.
              {!hasAutoRefresh && (
                <>
                  {" "}
                  Add a Token Issuer Token to enable
                  automatic refresh.
                </>
              )}
            </AlertDescription>
          </Alert>
        )}

        {tokenStatus?.status === "expiring" && (
          <Alert>
            <IconAlertTriangle className="size-4" />
            <AlertTitle>Token Expiring Soon</AlertTitle>
            <AlertDescription>
              The service account token expires in{" "}
              {tokenStatus.expiresInFormatted}.
              {hasAutoRefresh ? (
                <span className="mt-1 flex items-center gap-1">
                  <IconRefresh className="size-4 text-info-foreground" />
                  Auto-refresh is enabled - token will be
                  renewed automatically.
                </span>
              ) : (
                " Add a Token Issuer Token to enable automatic refresh."
              )}
            </AlertDescription>
          </Alert>
        )}

        {tokenStatus?.status === "valid" &&
          hasAutoRefresh && (
            <Alert>
              <IconRefresh className="size-4 text-info-foreground" />
              <AlertTitle>Auto-refresh Enabled</AlertTitle>
              <AlertDescription>
                Token is valid for{" "}
                {tokenStatus.expiresInFormatted} and will be
                renewed automatically before expiration.
              </AlertDescription>
            </Alert>
          )}

        <ClusterForm
          organizationId={organizationId}
          mode="edit"
          initialValues={initialValues}
          onSubmit={handleSubmit}
          isSubmitting={updateCluster.isPending}
          cardDescription="Update the Kubernetes cluster configuration."
          submitButtonText="Save Changes"
          submittingButtonText="Saving..."
        />
      </div>
    </AppLayout>
  );
}
